<div class="flex flex-col gap-y-10">

  <p class="text-sm text-gray">
    Optional. Add any individual securities to <strong><em>Buy</em></strong> into the advisor-directed Custom Holdings sleeve. Amounts are
    <strong>estimates</strong> based on the last reported price — the trade team executes on the share count,
    so the final value will differ.
  </p>

  <!-- Says where alternatives belong, on the step where they have to be entered. An advisor who
       does not know to enter them here never finds out.

       ⚠️ It no longer says what HAPPENS to them ("these will be included as protected cash").
       That sentence was true and still said too much for the top of a step: the per-row note
       says it the moment a ticker is actually entered, and it says it about that row rather than
       as a standing warning over every buy on the screen. -->
  <p class="text-sm text-gray">
    <strong>Note:</strong> Please include any CUSIPs of additional alternative investments on this step as
    well.
  </p>

  @if (store.buysExceedInvestable()) {
    <!-- ⚠️ The remedy differs by request type, and pointing at the wrong one is worse than
         pointing at nothing: a contribution has no sell step to send the advisor to, and the
         money it can spend is the contributed amount rather than anything in the account. -->
    <p class="px-15 py-10 text-sm border rounded border-red-light bg-red-lightest text-red-dark">
      <!-- ⚠️ The FIGURE is gone from this sentence, deliberately. It read "more than the $0.00
           available to invest", and $0.00 is the least informative number a message can contain --
           it states the problem as an arithmetic fact the advisor already knows. The strip above
           carries the real figure, signed and in red, on every step.

           ⚠️ It still names three places, and the model allocation is the one an advisor is most
           likely to reach for: the ceiling this compares against is what is left AFTER the models
           are funded, so pulling a model back frees buying power without selling anything. A
           message naming only positions reads as "sell more", which is a real instruction to give
           someone who simply over-allocated.

           ⚠️ A CONTRIBUTION HAS NO POSITIONS TO SELL -- its wizard has no Review/Sell step at
           all -- so the general wording would name a route that does not exist for it. Same
           sentence, one route shorter. -->
      @if (store.isContribution()) {
        Not enough funds are available to make these purchases. Correct the allocations to models
        or set-aside cash.
      } @else {
        Not enough funds are available to make these purchases. Correct the allocations to positions,
        models or set-aside cash.
      }
    </p>
  }

  @if (store.isContribution() && store.data()?.positions?.length) {
    <!-- What the sleeve already holds, read-only.

         A contribution does not restructure the account, so these rows are not decisions the
         advisor makes here -- there is no sell step and nothing to check. They are here because
         "buy additional securities" is only answerable against what is already held: without
         this, an advisor topping up an existing position has no way to see they hold it, and the
         obvious mistake is to buy a duplicate line of something already in the sleeve.

         Deliberately NOT the sell table with its controls hidden. Same rows, but a different
         question -- so a plain list, no checkboxes, no share inputs, and its own caption. -->
    <details class="border rounded border-gray-lighter bg-gray-lightest" open>
      <summary class="px-15 py-10 text-sm font-semibold cursor-pointer text-gray-darkest">
        Currently held in Custom Holdings
      </summary>
      <div class="w-full px-15 pb-10 overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-xs uppercase text-gray">
              <th class="p-8 text-left">Ticker</th>
              <th class="p-8 text-left">Description</th>
              <th class="p-8 text-right">Shares</th>
              <th class="p-8 text-right">Market Value</th>
              <!-- No caption: the cell holds either a button that names itself or a warning
                   icon, and "Action" over a column that is empty on some rows says nothing. -->
              <th class="p-8 text-right"><span class="sr-only">Buy more</span></th>
            </tr>
          </thead>
          <tbody>
            @for (p of store.positions(); track p.ticker) {
              <tr class="border-t border-gray-lighter">
                <td class="p-8 font-semibold text-gray-darkest">{{ p.ticker }}</td>
                <td class="p-8 text-gray-dark">{{ p.description }}</td>
                <td class="p-8 text-right text-gray-dark" style="font-variant-numeric: tabular-nums">
                  {{ p.shares !== null && p.shares !== undefined ? (p.shares | number) : '—' }}
                </td>
                <td class="p-8 text-right text-gray-dark" style="font-variant-numeric: tabular-nums">
                  {{ p.marketValue | currency }}
                </td>
                <!-- ⚠️ A BUTTON ONLY WHERE A PURCHASE IS POSSIBLE. Only Mutual Fund, CASH and
                     Equity can be bought through this step, so the structured notes and bonds an
                     account holds get the warning instead. Offering the button on those and
                     failing afterwards would be worse than not offering it.

                     ⚠️ The icon says WHY rather than the row simply being blank. An empty cell on
                     some rows and a button on others reads as a rendering fault; the same amber
                     ⚠️ used on step 1 for a position whose proceeds are held is the established
                     way this wizard says "this security is different". -->
                <td class="p-8 text-right">
                  @if (canBuyMore(p)) {
                    <button type="button" class="button is-mint is-sm"
                      [attr.aria-label]="'Buy more ' + p.ticker"
                      title="Add a buy row for this position"
                      (click)="buyMore(p)">Buy</button>
                  } @else {
                    <i class="material-icons cursor-help text-orange-darker" style="font-size: 1.5rem"
                      tabindex="0"
                      title="Additional positions of this security cannot be purchased in this request."
                      [attr.aria-label]="p.ticker + ': additional positions of this security cannot be purchased in this request'">warning_amber</i>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </details>
  }

  <div class="w-full overflow-x-auto">
    <table class="w-full text-sm border-collapse">
      <thead>
        <!-- ⚠️ The HEADER carries the emphasis, exactly as it does on step 1: uppercase, tracked
             and on its own tinted ground, so the table has a firm top edge. Same treatment on
             both steps because both are a list the advisor works down -- the two screens reading
             as one shape is the point, and this one had a plain header while step 1's popped.

             Deliberately GREY, not mint. Mint means "the request" everywhere in this wizard, and
             a mint table head would spend that meaning on furniture.

             `border-gray-light` on the cells, not `-lighter`: one weight of rule under the whole
             head, matching step 1. -->
        <tr class="text-xs tracking-wide uppercase text-gray-dark bg-gray-lightest">
          <th class="p-8 text-left border-b border-gray-light">Ticker/CUSIP</th>
          <!-- ⚠️ CENTRED, unlike Price / Share and Est. Amount either side of it, and the
               difference is deliberate: those two are figures, which are read down a right
               edge, while this column holds a control.

               It was right-aligned to match them, which put the label above the calculator
               button rather than above the box -- the input is the widest thing in the cell but
               the icon is what sits flush right, so the group's visual mass ended up left of
               its own header. Centring the label and the group ties the two together. -->
          <th class="p-8 text-center border-b border-gray-light">Shares</th>
          <th class="p-8 text-right border-b border-gray-light">Price / Share</th>
          <th class="p-8 text-right border-b border-gray-light">Est. Amount</th>
          <th class="p-8 border-b border-gray-light"></th>
        </tr>
      </thead>
      <tbody>
        @for (row of store.buys(); track row.id) {
          <!-- `data-buy-row` is the scroll target for "Buy more" on a held position above: the
               held list can run long enough that a row added at the bottom is off screen. -->
          <tr class="border-b border-gray-lightest" [attr.data-buy-row]="row.id">
            <td class="p-8 text-left align-top" style="min-width: 22rem" [attr.data-security-picker]="row.id">
              @if (row.security; as security) {
                <!-- Picked: show it, and let a click put the row back into search.

                     The pencil is always visible, not hover-only: the complaint this
                     answers was that the only apparent way to change a security was to
                     delete the row, and an affordance that appears only once you are
                     already hovering the right pixels does not fix that. Styled as text
                     with a hint rather than as a button, so the cell still reads as data.

                     Same global-button overrides as the result rows; without normal-case
                     the description renders shouting in caps. -->
                <button type="button" (click)="reopen(row.id)"
                  class="flex flex-col items-start h-auto px-0 text-left normal-case font-normal min-w-0"
                  title="Change security"
                  [attr.aria-label]="'Change security, currently ' + security.ticker">
                  <span class="flex items-center gap-x-4">
                    <span class="font-semibold"
                      [class.text-gray-darkest]="!row.conflictsWithSell && !row.isDuplicate"
                      [class.text-red-dark]="row.conflictsWithSell || row.isDuplicate">{{ security.ticker }}</span>
                    <i class="material-icons text-gray-light" style="font-size: 1.4rem" aria-hidden="true">edit</i>
                  </span>
                  @if (security.description) {
                    <span class="text-xs text-gray-dark">{{ security.description }}</span>
                  }
                </button>
                @if (row.conflictsWithSell) {
                  <span class="block mt-2 text-xs text-red-dark">
                    Selling and buying the same security is prohibited in this request. You may only buy
                    more of the same security.
                  </span>
                }

                @if (row.isDuplicate) {
                  <span class="block mt-2 text-xs text-red-dark">
                    {{ security.ticker }} is already on another row. Combine the shares into one row.
                  </span>
                }

                <!--
                  Hand-typed ticker: an alert panel, matching the allocation step's, but
                  orange rather than red. It is not an error — the request is valid and
                  Next is not blocked — so red would tell the advisor to go back and fix
                  something that does not need fixing. What it has to do is stop the row
                  reading as a resolved buy, since the trade team is who acts on it.
                -->
                @if (row.needsTradeTeamReview) {
                  <!-- px-10, not pl-16/pr-12: 16 and 12 are not on the FIG spacing scale
                       (…6, 8, 10, 15, 18, 20…), so those classes generate no CSS at all
                       and the icon sat flush against the border. Same trap the tailwind
                       config records for w-250. -->
                  <div class="flex items-start px-10 py-8 mt-5 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
                    <i class="material-icons" style="font-size: 1.4rem" aria-hidden="true">info_outline</i>
                    <!-- Says the consequence, not just the fact. The trade team cannot be given a
                         security nobody has identified, so this row cannot be executed as a buy --
                         its money is held back until they resolve the ticker.

                         ⚠️ It says the money is held IN CASH ON THE ACCOUNT, not "set aside as
                         protected cash" (2026-09-15). Two reasons the old wording had to go: the
                         advisor's own step-4 set-aside is now the only thing called a set-aside,
                         so reusing the phrase here claimed they had done something they had not;
                         and "protected cash" named a step this hold is not reported on. What the
                         advisor needs to know is where the money sits and what clears it. -->
                    <span>
                      <strong>{{ security.ticker }}</strong> is not a security on file and requires review by the
                      Trade Team. This amount will be held in cash on the account until the buy can be resolved.
                    </span>
                  </div>
                }
              } @else {
                <!-- No security yet: an input, ready to type. No button to press first.

                     autocomplete="off" matters here rather than being boilerplate: the
                     browser's own history would offer back tickers previously typed into
                     this box — including ones that never matched anything — which is the
                     one suggestion list that is not ours to honour. -->
                <input #securityInput type="text" class="w-full text-left input is-sm"
                  role="combobox" [attr.aria-expanded]="searchingRowId() === row.id"
                  autocomplete="off"
                  [class.border-red-dark]="row.needsSecurity"
                  placeholder="Type a ticker/cusip" aria-label="Search for a ticker, or add a custom ticker or CUSIP"
                  [value]="termFor(row.id)"
                  (input)="onSearchInput(row.id, $event)"
                  (focus)="onSearchFocus(row.id)"
                  (keydown)="onSearchKeydown(row.id, $event)" />
                @if (row.needsSecurity) {
                  <!-- Two ways to arrive here and they need different words. Text left in the box
                       means the advisor typed something and never chose -- telling them to
                       "enter a ticker" would describe what they can see they already did. -->
                  <span class="block mt-2 text-xs text-red-dark">
                    @if (row.pendingTerm) {
                      Choose a security from the list, or add &ldquo;{{ row.pendingTerm }}&rdquo; manually.
                    } @else {
                      Enter a ticker for these shares
                    }
                  </span>
                }

                <!-- The how-to, not a placeholder: it has to stay readable while the advisor is
                     typing. Stood down while the dropdown for this row is open, so it never sits
                     between the box and its own results, and while the row is in error, so one
                     line speaks at a time.

                     ⚠️ No longer mentions Enter. Enter used to commit whatever was in the box
                     when nothing matched; the only way to hand-enter a ticker now is the option
                     at the foot of the list, and telling advisors to press Enter would teach
                     the one gesture that was removed. -->
                @if (!row.needsSecurity && searchingRowId() !== row.id) {
                  <span class="block mt-2 text-xs text-gray">Search for a ticker or CUSIP, then choose it from the list.</span>
                }

                @if (searchingRowId() === row.id) {
                  @if (isSearching()) {
                    <span class="block mt-2 text-xs text-gray">Searching…</span>
                  } @else if (options().length) {
                    <div class="mt-2 overflow-y-auto border rounded border-gray-lighter bg-white" role="listbox"
                      style="max-height: 18rem">
                      @for (option of options(); track $index; let i = $index) {
                        @switch (option.kind) {
                          @case ('security') {
                            <!-- items-start / normal-case / font-normal / h-auto / min-w-0 all
                                 undo the global `button` rule in styles.input.scss, which is
                                 written for chunky action buttons. items-start is the one that
                                 matters: with flex-col it governs the HORIZONTAL axis, so the
                                 global items-center centres these and no amount of text-left
                                 fixes it. h-34 would also clip the second line. -->
                            <button type="button" role="option" [attr.aria-selected]="i === highlighted()"
                              class="flex flex-col items-start w-full h-auto px-10 py-5 text-left normal-case font-normal min-w-0"
                              [class.bg-mint-lightest]="i === highlighted()"
                              (mouseenter)="moveHighlight(i)"
                              (click)="pick(row.id, option.security)">
                              <span class="font-semibold text-gray-darkest">{{ option.security.ticker }}</span>
                              <span class="text-xs text-gray-dark">{{ option.security.description }}</span>
                            </button>
                          }
                          @case ('manual') {
                            <!-- ⚠️ THE ESCAPE HATCH, and the only way a row becomes hand-typed.
                                 FIRST in the list: the box scrolls at 18rem, so anchored at the
                                 bottom it hid below the fold on exactly the searches that return
                                 a long list of near-misses.

                                 ⚠️ It is NOT highlighted by default, and its position is not
                                 what guarantees that -- `highlighted` seeks the first SECURITY,
                                 so with results the highlight starts one row BELOW this, and
                                 with none it stays at -1 and this waits to be clicked or
                                 arrowed onto. That is the friction the whole change exists for.

                                 Quotes the term back rather than saying "add manually": a
                                 transposed CUSIP is only visible if it is shown.

                                 border-b and the tinted ground separate it from the securities
                                 below -- it is an action, not another search result. -->
                            <button type="button" role="option" [attr.aria-selected]="i === highlighted()"
                              class="flex flex-col items-start w-full h-auto px-10 py-8 text-left normal-case font-normal min-w-0 border-b border-gray-lighter"
                              [class.bg-mint-lightest]="i === highlighted()"
                              [class.bg-gray-lightest]="i !== highlighted()"
                              (mouseenter)="moveHighlight(i)"
                              (click)="addManually(row.id)">
                              <span class="flex items-center font-semibold gap-x-5 text-mint-darker">
                                <i class="material-icons" style="font-size: 1.6rem" aria-hidden="true">add</i>
                                Add &ldquo;{{ termFor(row.id).trim().toUpperCase() }}&rdquo; manually
                              </span>
                              <!-- Says the consequence HERE, where the decision is, rather than
                                   only in the orange panel that appears afterwards. -->
                              <span class="text-xs text-gray-dark">
                                Not a security on file — held as protected cash until the Trade Team resolves it
                              </span>
                            </button>
                          }
                        }
                      }
                    </div>
                  }
                }
              }
            </td>
            <td class="p-8 text-center align-top">
              <!-- Locked until a security comes back from the SEARCH. Shares are only
                   meaningful against something Orion priced, and a count typed before a
                   ticker was picked had nothing to attach to.

                   A hand-typed ticker never unlocks it - that row is entered by amount
                   instead, in the Est. Amount column. -->
              @if (row.needsTradeTeamReview) {
                <div class="flex items-center justify-center" style="min-height: 2.8rem">
                  <span class="text-gray-light" title="Entered as an amount instead — this ticker has no price to convert from">—</span>
                </div>
              } @else {
                <!-- The calculator sits with the box it fills, to its right.

                     Offered only on a priced security, because there is nothing to divide by
                     otherwise -- and a button that does nothing on half the rows is worse than
                     one that appears where it works.

                     ⚠️ Its slot is reserved even when the button is absent, and that is what
                     keeps the INPUTS aligned down the column. The group is centred, so without
                     the spacer an unpriced row's box would centre on its own while a priced
                     row's sat left of centre by half the button's width -- the boxes would step
                     in and out down the column. Same reasoning as the Sell All pill on Review:
                     reserve the varying part, not the figure. -->
                <!-- ⚠️ items-START, not items-center: the error text below the box makes this
                     column taller than the icon beside it, and centring would then float the
                     icon down away from the input it belongs to. -->
                <div class="flex items-start justify-center gap-x-5">
                  <!-- The box and its error share a column, so the message sits under the box
                       rather than centred across the box AND the icon -- which read as though it
                       belonged to neither.

                       ⚠️ Fixed at the input's own width with the message allowed to overflow it
                       (nowrap). Letting the column grow to fit the text would widen the whole
                       centred group, so the input on an errored row would sit left of every
                       other row's -- the alignment the reserved icon slot exists to protect.
                       Overflowing costs nothing here: the neighbouring Est. Amount error has
                       always done the same, and the message still starts at the box's edge. -->
                  <div class="flex flex-col w-96">
                    <input type="number" min="0" step="any" class="w-full text-right input is-sm no-spinner"
                      appSelectOnFocus
                      [disabled]="!row.security"
                      [class.bg-gray-lightest]="!row.security"
                      [class.border-red-dark]="row.needsShares"
                      [value]="row.shares || ''" (change)="onShares(row.id, $event)"
                      [attr.aria-label]="'Shares to buy'" [attr.data-shares-input]="row.id" />
                    @if (row.needsShares) {
                      <span class="block mt-2 text-xs text-left text-red-dark whitespace-nowrap">
                        Enter a share count
                      </span>
                    }
                  </div>
                  <span class="flex items-center justify-center"
                    style="height: 2.8rem; width: 2.8rem; min-width: 2.8rem">
                    @if (canCalculateShares(row)) {
                      <!-- Drawn inline rather than loaded, and that is what lets it take the
                           mint token: `fill="currentColor"` inherits `text-mint-darker` from the
                           button, so it tracks the theme instead of baking a colour into a file
                           nobody would think to re-export. The Material font had no calculator
                           that read as one at this size.

                           ⚠️ ONE path with `fill-rule="evenodd"`, so the screen and the nine keys
                           are HOLES rather than white shapes. Painting them white would look
                           right on this table and wrong the moment the row is tinted -- the
                           allocation grid already bands its cells, and a disabled row here is
                           already `bg-gray-lightest`.

                           A mask would do the same job but needs an id, and this renders once
                           per row -- nine identical ids in the DOM is a mess even where it
                           happens to resolve.

                           Geometry is the reference art scaled to a 16x24 box: body radius 3,
                           screen inset to the key columns' outer edges, keys r1.5 on centres
                           3.6/8/12.4 by 11.3/15.5/19.7. -->
                      <button type="button"
                        class="flex items-center justify-center px-0 text-mint-darker hover:opacity-75"
                        style="height: 2.8rem; width: 2.8rem; min-width: 2.8rem"
                        title="Work out the shares from a dollar amount"
                        [attr.aria-label]="'Work out shares from an amount for ' + row.security?.ticker"
                        (click)="openShareCalculator(row)">
                        <svg viewBox="0 0 16 24" style="height: 2rem; width: 1.34rem"
                          fill="currentColor" fill-rule="evenodd" aria-hidden="true" focusable="false">
                          <path d="M3 0 H13 A3 3 0 0 1 16 3 V21 A3 3 0 0 1 13 24 H3 A3 3 0 0 1 0 21 V3 A3 3 0 0 1 3 0 Z
                                   M3.6 2.4 H12.4 A1.4 1.4 0 0 1 13.8 3.8 V6.2 A1.4 1.4 0 0 1 12.4 7.6 H3.6 A1.4 1.4 0 0 1 2.2 6.2 V3.8 A1.4 1.4 0 0 1 3.6 2.4 Z
                                   M2.1 11.3 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M6.5 11.3 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M10.9 11.3 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M2.1 15.5 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M6.5 15.5 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M10.9 15.5 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M2.1 19.7 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M6.5 19.7 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M10.9 19.7 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z" />
                        </svg>
                      </button>
                    }
                  </span>
                </div>
              }
            </td>
            <!-- Every cell is top-aligned and its primary content sits in a 2.8rem line
                 box, matching the .input.is-sm height. So the inputs, the prices and the
                 remove button all line up, and the error labels hang below without
                 dragging the rest of the row down with them. -->
            <td class="p-8 text-right align-top">
              @if (row.security; as security) {
                <div class="flex items-center justify-end" style="min-height: 2.8rem">
                  <!-- A hand-typed ticker has no price, and a blank cell would read as a
                       load that had not finished. An em dash says there is nothing to
                       show, which is the honest answer. -->
                  @if (security.price != null) {
                    <span class="text-gray-darkest">{{ security.price | currency }}</span>
                  } @else {
                    <span class="text-gray-light">—</span>
                  }
                </div>
                <!-- `priceAsOf` is deliberately not shown here, but IS still captured and
                     submitted: it is what makes the estimate auditable later, and Review &
                     Submit prints it in its own Priced column. -->
              } @else {
                <div class="flex items-center justify-end" style="min-height: 2.8rem">
                  <span class="text-gray-light">—</span>
                </div>
              }
            </td>
            <td class="p-8 text-right align-top">
              <!-- The one editable figure on a hand-typed row. Nothing priced the ticker,
                   so shares cannot be turned into dollars - the advisor states the dollars
                   and the trade team works back to shares once it resolves the security. -->
              @if (row.needsTradeTeamReview) {
                <!-- The $ sits outside the box, as it does on Protected Cash: a number input
                     cannot carry a prefix of its own, and typing one into the field would make
                     the value non-numeric. -->
                <div class="flex items-center justify-end gap-x-5">
                  <span class="text-gray-darkest">$</span>
                  <input type="number" min="0" step="any" class="w-96 text-right input is-sm no-spinner"
                    appSelectOnFocus appTwoDecimals
                    [class.border-red-dark]="row.needsAmount"
                    [value]="amountInput(row.manualAmount)" (change)="onAmount(row.id, $event)"
                    [attr.aria-label]="'Estimated amount to buy'" [attr.data-amount-input]="row.id" />
                </div>
                @if (row.needsAmount) {
                  <span class="block mt-2 text-xs text-red-dark">Enter an amount</span>
                }
              } @else {
                <div class="flex items-center justify-end" style="min-height: 2.8rem">
                  <!-- No price means no estimate. "$0.00" would be a number the advisor
                       could reasonably read as the buy's worth, so it must not appear. -->
                  @if (row.isActionable) {
                    <span class="text-gray-darkest">{{ row.estimatedAmount | currency }}</span>
                  } @else {
                    <span class="text-gray-light">$0.00</span>
                  }
                </div>
              }
            </td>
            <td class="p-8 text-center align-top">
              <div class="flex items-center justify-center" style="min-height: 2.8rem">
                <button type="button" class="icon-button is-alert is-inverted" title="Remove buy"
                  (click)="store.removeBuyRow(row.id)">
                  <i class="material-icons" style="font-size: 1.6rem">delete</i>
                </button>
              </div>
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="5" class="p-15 text-sm text-center text-gray">
              <!-- Names what the advisor is doing INSTEAD, which differs by type: a contribution
                   has nothing to sell, so "if you are only selling" describes a step it does
                   not have. -->
              @if (store.isContribution()) {
                No buys added. This step is optional — continue if the contribution is going to
                the models or staying as cash.
              } @else {
                No buys added. This step is optional — continue if you are only selling or reallocating.
              }
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  <div>
    <!-- Same classes as the allocation step's Add Model: the two steps are the same gesture
         on the same kind of table, so they get the same button. SOLID mint, as the primary
         action here -- outlined, it read as a link rather than the control that starts the
         only work this step does. -->
    <button type="button" class="button is-mint is-sm" (click)="addRow()">
      + Add Buy
    </button>
  </div>
</div>
